import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BackButton } from "../../../../components/buttons/backButton";
import { Button } from "../../../../components/buttons/button";
import { InfoCard } from "../../../../components/cards/infoCard";
import {
  EventCard,
  EventCardSkeleton,
} from "../../../../components/event/eventCard";
import {
  FlagEventModal,
  UnFlagEventModal,
} from "../../../../components/modal/app_modals/flagEventModal";
import { TabNav } from "../../../../components/tabNav";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import {
  useAdminGetEventGuestsQuery,
  useAdminGetEventTicketsBreakdownQuery,
  useAdminGetEventTicketsQuery,
} from "../../../../redux/features/admin/event/events.api";
import { useGetEventQuery } from "../../../../redux/features/event/event.api";
import { PayoutCurrency } from "../../../../redux/features/payment/payment.types";
import {
  eventCreatorPermissions,
  TICKET_COLOR_OPTIONS,
} from "../../../../utils/constants";
import {
  formatDate,
  formatTime,
  getEventCurrencyFromTickets,
  getEventUrl,
  getRandomColor,
  parseTimeToDate,
} from "../../../../utils/helpers";
import { TicketSalesBreakdown } from "../../../../utils/types";
import { EventGuests } from "../../../events/view/components/eventGuests";
import { AdminEventGuestView } from "./components/guestView";
import { AdminEventOverview } from "./components/overview";
import { AdminEventTickets } from "./components/tickets";

export type Tabs = "overview" | "guests" | "tickets";

export const AdminViewSingleEvent = ({
  guestView = false,
}: {
  guestView?: boolean;
}) => {
  const [activeTab, setActiveTab] = useState<Tabs>("overview");
  const [isPaidEvent, setIsPaidEvent] = useState<boolean>(false);
  const [ticketBreakdown, setTicketBreakdown] = useState<TicketSalesBreakdown>(
    [],
  );
  const [statusFlags, setStatusFlags] = useState({
    isCancelled: false,
    isFlagged: false,
  });
  const [eventCurrency, setEventCurrency] = useState<PayoutCurrency>();

  const tabList = [
    {
      title: "Overview",
      to: "overview",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
    {
      title: "Guests",
      to: "guests",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
    {
      title: "Tickets",
      to: "tickets",
      onClick: (selected: string) => {
        setActiveTab(selected as Tabs);
      },
    },
  ];

  const { id, guestId } = useParams<{ id: string; guestId: string }>();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: eventResponse,
    isLoading: fetchingEvenntDetails,
    refetch: refetchEventDetails,
  } = useGetEventQuery(
    {
      id: id,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: ticketPurchaseBreakdown } =
    useAdminGetEventTicketsBreakdownQuery(
      {
        id: id!,
      },
      { refetchOnMountOrArgChange: true, skip: !id },
    );

  const { data: guests, isFetching: fetchingGuests } =
    useAdminGetEventGuestsQuery(
      {
        id: id!,
        search: "",
        page: 1,
        limit: 50,
        sortOrder: "desc",
      },
      { refetchOnMountOrArgChange: true, skip: !id || guestView },
    );

  const { data: ticketsData, isFetching: fetchingTickets } =
    useAdminGetEventTicketsQuery(
      {
        id: id!,
      },
      { refetchOnMountOrArgChange: true, skip: !id || guestView },
    );

  useEffect(() => {
    setIsPaidEvent(
      ticketsData?.data?.tickets?.some((t) => t.ticketType === "paid") ?? false,
    );

    if ((ticketsData?.data?.totalCount ?? 0) > 0) {
      // Use NGN as default currency to accommodate pre-currency introduction
      setEventCurrency(
        getEventCurrencyFromTickets(ticketsData?.data?.tickets!) ?? "NGN",
      );
    }
  }, [ticketsData]);

  useEffect(() => {
    const selectedColors: string[] = [];

    if (ticketPurchaseBreakdown?.data) {
      setTicketBreakdown(
        ticketPurchaseBreakdown.data.map((t) => {
          const color = getRandomColor(
            TICKET_COLOR_OPTIONS,
            selectedColors,
            "sequential",
          );
          selectedColors.push(color);
          return {
            ticketId: t.ticketId,
            purchaseQuantity: t.totalQuantityPurchased,
            color: color,
          };
        }),
      );
    }
  }, [ticketPurchaseBreakdown]);

  useEffect(() => {
    if (eventResponse?.data) {
      setStatusFlags((s) => ({
        ...s,
        isCancelled: eventResponse.data.status === "cancelled",
        isFlagged: !!eventResponse.data.isFlagged,
      }));
    }
  }, [eventResponse]);

  return fetchingEvenntDetails ? (
    <EventCardSkeleton imageDimension={300} showExtraDetails className="" />
  ) : (
    <div className="flex w-full animate-fade-in flex-col gap-2">
      <BackButton />

      {statusFlags.isFlagged && (
        <InfoCard
          className="!bg-lighter-red"
          leftIcon="flag"
          title="Flagged Event"
          text={
            <>
              This event was flagged for{" "}
              <b>{eventResponse?.data?.flaggedReason}</b> on{" "}
              {formatDate(eventResponse?.data?.flaggedDate)}
            </>
          }
        />
      )}

      {statusFlags.isCancelled && (
        <InfoCard
          className="!bg-lighter-red"
          leftIcon="bin-2"
          leftClassName="!text-danger"
          title={<p className="text-danger">Event Deleted</p>}
          text={<>Deleted on {formatDate(eventResponse?.data?.deletedDate)}</>}
        />
      )}

      <EventCard
        image={eventResponse?.data?.image!}
        name={eventResponse?.data?.name!}
        location={eventResponse?.data?.location!}
        startDate={formatDate(
          eventResponse?.data?.startDate!,
          "ddd, MMMM DD, YYYY",
          true,
        )}
        endDate={formatDate(
          eventResponse?.data?.endDate!,
          "ddd, MMMM DD, YYYY",
          true,
        )}
        startTime={formatTime(parseTimeToDate(eventResponse?.data?.startTime!))}
        endTime={formatTime(parseTimeToDate(eventResponse?.data?.endTime!))}
        noTickets={undefined}
        totalGuests={guests?.data?.totalCount ?? 0}
        isOrganizer={eventResponse?.data?.userType === "organiser"}
        event={eventResponse?.data!}
        className=""
        imageDimension={300}
        hasPaidTickets={isPaidEvent}
        showUserTypePill={guestView}
        showStatusPill
        showLocationPill
        showTicketPill={!guestView}
        showCreatedOn
      >
        {!guestView && (
          <div className="flex flex-col items-center gap-[10px] md:flex-row">
            {!statusFlags.isFlagged && !statusFlags.isCancelled ? (
              <Button
                type="button"
                onClick={() => {
                  window.open(
                    getEventUrl(eventResponse?.data?.slug!),
                    "_blank",
                  );
                }}
                text="Go To Event"
                icon="share"
                variation="plain-grey"
                className=""
                disabled={false}
              />
            ) : undefined}

            {!statusFlags.isCancelled ? (
              <Button
                type="button"
                onClick={() => {
                  if (statusFlags.isFlagged) {
                    handleModalOpen("unflagEventModal");
                  } else {
                    handleModalOpen("flagEventModal");
                  }
                }}
                text={
                  statusFlags.isFlagged ? "Remove Event Flag" : "Flag Event"
                }
                icon="flag"
                variation="plain-grey"
                className=""
                disabled={false}
              />
            ) : undefined}
          </div>
        )}
      </EventCard>

      <div className="flex flex-col gap-[2px]">
        {guestView ? (
          <BgWrapper>
            <MiniHeader header="Guests" />
          </BgWrapper>
        ) : (
          <BgWrapper className="!rounded-b-none">
            <TabNav
              navs={tabList}
              activeTab={activeTab}
              variant="switcher"
              className=""
              tabClassName=""
            />
          </BgWrapper>
        )}

        {guestView && (
          <AdminEventGuestView
            guestId={guestId ?? ""}
            ticketBreakdown={ticketBreakdown}
          />
        )}

        {activeTab === "overview" && !guestView ? (
          <AdminEventOverview
            event={eventResponse?.data!}
            setTab={setActiveTab}
            ticketsData={ticketsData}
            eventCurrency={eventCurrency}
            guests={guests}
            loading={fetchingGuests || fetchingEvenntDetails || fetchingTickets}
            refetchEventDetails={() => {
              refetchEventDetails();
            }}
          />
        ) : undefined}

        {activeTab === "guests" &&
        !guestView &&
        eventResponse?.data !== undefined ? (
          <EventGuests
            event={eventResponse?.data!}
            ticketBreakdown={ticketBreakdown}
            adminView={true}
            eventCurrency={eventCurrency!}
            userEventPermissions={eventCreatorPermissions}
          />
        ) : undefined}

        {activeTab === "tickets" &&
        !guestView &&
        eventResponse?.data !== undefined ? (
          <AdminEventTickets
            event={eventResponse?.data!}
            data={ticketsData}
            fetchingTickets={fetchingTickets}
            eventCurrency={eventCurrency!}
          />
        ) : undefined}
      </div>

      {id && (
        <FlagEventModal
          open={modalOpen && modal === "flagEventModal"}
          handleClose={() => {
            handleModalClose();
          }}
          eventId={id}
          onSuccess={() => {
            refetchEventDetails();
          }}
        />
      )}

      {id && (
        <UnFlagEventModal
          open={modalOpen && modal === "unflagEventModal"}
          handleClose={() => {
            handleModalClose();
          }}
          eventId={id}
          onSuccess={() => {
            refetchEventDetails();
          }}
        />
      )}
    </div>
  );
};
